import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, FolderKanban } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Input, Label } from '@/components/ui/Input';
import { Spinner, EmptyState, Badge, Alert } from '@/components/ui/misc';
import { Modal } from '@/components/ui/Modal';
import { api, ApiError } from '@/lib/api';
import { formatDate } from '@/lib/utils';

export function Projects() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [brand, setBrand] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['projects'], queryFn: () => api.listProjects() });

  const createMut = useMutation({
    mutationFn: () =>
      api.createProject({ name: name || url, websiteUrl: url, brandName: brand || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setOpen(false);
      setName('');
      setUrl('');
      setBrand('');
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not create project.'),
  });

  const projects = data?.projects ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">Projects</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Organize the brands and websites you track.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus size={18} /> New project
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="h-7 w-7" />
        </div>
      ) : projects.length === 0 ? (
        <EmptyState
          icon={<FolderKanban size={28} />}
          title="No projects yet"
          description="Create a project or run a new analysis to get started."
          action={
            <Button onClick={() => setOpen(true)}>
              <Plus size={16} /> New project
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <Link key={p.id} to={`/app/projects/${p.id}`}>
              <Card className="h-full transition hover:border-accent-blue/40">
                <CardContent className="pt-5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-text-primary">{p.name}</h3>
                    {p.industry && <Badge tone="blue">{p.industry}</Badge>}
                  </div>
                  <p className="mt-1 truncate text-sm text-text-secondary">{p.websiteUrl}</p>
                  {p.competitors.length > 0 && (
                    <p className="mt-3 text-xs text-text-muted">
                      {p.competitors.length} competitor{p.competitors.length === 1 ? '' : 's'}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-text-muted">Updated {formatDate(p.updatedAt)}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New project">
        <div className="space-y-4">
          <div>
            <Label htmlFor="p-name">Project name</Label>
            <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="p-url">Website URL</Label>
            <Input
              id="p-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
            />
          </div>
          <div>
            <Label htmlFor="p-brand">Brand name (optional)</Label>
            <Input id="p-brand" value={brand} onChange={(e) => setBrand(e.target.value)} />
          </div>
          {error && <Alert tone="error">{error}</Alert>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMut.mutate()}
              disabled={!url || createMut.isPending}
            >
              {createMut.isPending ? <Spinner /> : 'Create project'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
